import {
  addMilliseconds,
  differenceInYears,
  isAfter,
  isSameDay,
  startOfDay,
} from 'date-fns';
import { useMemo } from 'react';
import { useToday } from 'shared/contexts/today';
import { useInsightTimeframePerfectSlots } from 'shared/hooks/use-insight-timeframe-perfect-slots';
import { useTimeframeOccurrences } from 'shared/hooks/use-timeframe-occurrences';
import { isBetween } from 'shared/lib/date-fns';
import { DateBarChartEntry } from 'shared/lib/recharts';
import { Habit } from 'shared/types/habit';
import {
  FrequencyUnit,
  HabitSchedule,
  ScheduleEntryState,
} from 'shared/types/habit-schedule';
import { InsightsTimeframes } from 'shared/types/insights';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { getActiveSchedule } from 'shared/utils/get-active-schedule';
import { getDateBarTooltip } from 'shared/utils/get-date-bar-tooltip';
import { getDateXAxisLabel } from 'shared/utils/get-date-x-axis-label';
import { getScheduleFrequency } from 'shared/utils/get-schedule-frequency';
import { getScheduleTimeframeTarget } from 'shared/utils/get-schedule-timeframe-target';
import {
  getTimeframesForDateRange,
  Interval,
} from 'shared/utils/get-timeframes-for-date-range';
import {
  getActualScheduleStartDate,
  getScheduleEndDate,
  getTimeframeSchedules,
} from 'shared/utils/schedules';

export type Options = {
  timeframe: InsightsTimeframes;
  startDate: Timestamp;
  endDate: Timestamp;
  weekStartsOn?: WeekDays;
};

export const useHabitBarChartEntries = (
  habit: Habit,
  { timeframe, startDate, endDate, weekStartsOn = WeekDays.Monday }: Options,
) => {
  const today = useToday();
  const { occurrences } = useTimeframeOccurrences(habit.schedules, {
    startDate,
    endDate,
  });
  const perfectSlots = useInsightTimeframePerfectSlots(habit.schedules, {
    timeframe,
    startDate,
    endDate,
    weekStartsOn,
  });

  const activeSchedule = useMemo(
    () => getActiveSchedule(habit.schedules),
    [habit.schedules],
  );
  const entries = useMemo((): DateBarChartEntry[] => {
    // get the active schedule to base the entry-range upon
    const activeSchedule = getActiveSchedule(habit.schedules);
    if (!activeSchedule) {
      return [];
    }
    const frequency = getScheduleFrequency(activeSchedule);
    const timeframeIntervalMap = {
      [InsightsTimeframes.Week]: Interval.Day,
      [InsightsTimeframes.Month]:
        activeSchedule.frequency?.unit === FrequencyUnit.Week
          ? Interval.Week
          : Interval.Month,
      [InsightsTimeframes.Quarter]:
        frequency === FrequencyUnit.Week ? Interval.Week : Interval.Month,
      [InsightsTimeframes.Year]: Interval.Month,
      [InsightsTimeframes.FourWeeks]: Interval.Week,
      [InsightsTimeframes.TwelveWeeks]: Interval.Week,
      [InsightsTimeframes.TwelveMonths]: Interval.Month,
    };
    // get all dates between start and end-date
    const timeframes = getTimeframesForDateRange(startDate, endDate, {
      weekStartsOn,
      interval: timeframeIntervalMap[timeframe],
    });

    // create empty entries for the timeframes
    return timeframes.map<DateBarChartEntry>((timeframeEntry) => {
      const schedulesForTimeframe = getTimeframeSchedules(habit.schedules, {
        startDate: timeframeEntry.startDate,
        endDate: timeframeEntry.endDate,
      });
      const { timeframeCompletions, timeframeTarget } =
        schedulesForTimeframe.reduce(
          (acc, schedule) => {
            // completions
            const scheduleCompletions = schedule.entries.filter(
              ({ state }) => state === ScheduleEntryState.Complete,
            );
            // get all completions within the timeframe
            const scheduleCompletionsForTimeframe =
              scheduleCompletions.filter(({ date }) =>
                isBetween(
                  date,
                  timeframeEntry.startDate,
                  timeframeEntry.endDate,
                ),
              ) ?? [];

            // target
            // get the start of the range of the schedule
            const scheduleStartDate = getActualScheduleStartDate(schedule);
            const scheduleEndDate = getScheduleEndDate(schedule);

            let target = 0;
            // get the start and end of the timeframe, based on schedule duration
            const _start = new Date(
              Math.max(
                timeframeEntry.startDate.getTime(),
                startOfDay(scheduleStartDate).getTime(),
              ),
            );
            const _end = scheduleEndDate
              ? new Date(
                  Math.min(
                    scheduleEndDate.getTime(),
                    timeframeEntry.endDate.getTime(),
                  ),
                )
              : timeframeEntry.endDate;

            // get the target for the schedule, for this timeframe
            target = schedule.frequency
              ? getScheduleTimeframeTarget(schedule, {
                  startDate: _start,
                  endDate: _end,
                  weekStartsOn,
                })
              : occurrences.filter((date) => isBetween(date, _start, _end))
                  .length * schedule.completionTarget.count;

            acc.timeframeCompletions +=
              scheduleCompletionsForTimeframe.reduce<number>(
                (completionAcc, { value }) => completionAcc + (value ?? 0),
                0,
              );
            acc.timeframeTarget += target;
            return acc;
          },
          { timeframeCompletions: 0, timeframeTarget: 0 },
        );
      return {
        date: timeframeEntry.referenceDate,
        value: timeframeCompletions,
        target: timeframeTarget,
        isFaded: isAfter(timeframeEntry.referenceDate, today),
        isPerfect: !!perfectSlots.find(
          (slot) =>
            (timeframe === InsightsTimeframes.Week &&
              isBetween(
                timeframeEntry.startDate,
                slot.startDate,
                slot.endDate,
              )) ||
            (isSameDay(timeframeEntry.startDate, slot.startDate) &&
              isSameDay(timeframeEntry.endDate, slot.endDate)),
        ),
        label: getDateXAxisLabel(timeframeEntry.referenceDate, {
          startDate,
          endDate,
          weekStartsOn,
        }),
        tooltip: getDateBarTooltip({
          value: timeframeCompletions,
          target: timeframeTarget,
          timeframe: timeframeEntry,
        }),
      };
    });
  }, [
    endDate,
    habit.schedules,
    occurrences,
    perfectSlots,
    startDate,
    timeframe,
    today,
    weekStartsOn,
  ]);

  const entriesTarget = useMemo(
    () => Math.max(...entries.map(({ target }) => target), 1),
    [entries],
  );

  const entriesValue = useMemo(
    () => entries.reduce((acc, { value }) => acc + value, 0),
    [entries],
  );

  // statistics
  const { totalEntriesTarget, completionCount } = useMemo(
    () =>
      entries.reduce(
        (acc, { value, target }) => {
          acc.completionCount += value;
          acc.totalEntriesTarget += target ?? 0;
          return acc;
        },
        { totalEntriesTarget: 0, completionCount: 0 },
      ),
    [entries],
  );
  const yearTarget = useMemo(() => {
    const timeframeSchedules = getTimeframeSchedules(habit.schedules, {
      startDate: startDate,
      endDate: endDate,
    });
    const lastSchedule = timeframeSchedules.reduce<HabitSchedule | undefined>(
      (acc, current) =>
        acc && isAfter(current.startDate, acc.startDate)
          ? current
          : acc ?? current,
      undefined,
    );
    if (lastSchedule?.frequency?.unit === FrequencyUnit.Year) {
      // adding a millisecond to make sure the start- and end-date are in different years when selecting a perfect year
      const years = differenceInYears(addMilliseconds(endDate, 1), startDate);
      return lastSchedule?.frequency.count * years;
    }

    return 0;
  }, [endDate, habit.schedules, startDate]);

  const timeframeTargetMap = {
    [InsightsTimeframes.Week]:
      activeSchedule?.frequency?.unit === FrequencyUnit.Week
        ? activeSchedule.frequency.count
        : totalEntriesTarget,
    [InsightsTimeframes.Month]:
      activeSchedule?.frequency?.unit === FrequencyUnit.Month
        ? activeSchedule.frequency.count
        : totalEntriesTarget,
    [InsightsTimeframes.Quarter]: totalEntriesTarget,
    [InsightsTimeframes.Year]:
      activeSchedule?.frequency?.unit === FrequencyUnit.Year
        ? yearTarget
        : totalEntriesTarget,
    [InsightsTimeframes.FourWeeks]: totalEntriesTarget,
    [InsightsTimeframes.TwelveWeeks]: totalEntriesTarget,
    [InsightsTimeframes.TwelveMonths]: totalEntriesTarget,
  };

  const target = timeframeTargetMap[timeframe];

  const completionPercentage = target
    ? Math.round((completionCount / target) * 1000) / 10
    : undefined;

  return {
    entries,
    perfectSlots,
    entriesValue,
    entriesTarget,
    target,
    completionPercentage:
      completionPercentage && completionPercentage >= 10
        ? Math.round(completionPercentage)
        : completionPercentage,
  };
};
